/**
 * Custom marks for the water comparisons.
 *
 * Drawn rather than sourced from an icon set, because each one has to read at
 * a glance as a specific Minnesota thing — the golf course in this town, the
 * town's own wells, the ski hill in the next county, a section of irrigated
 * corn. Generic pictograms would flatten exactly the local legibility that
 * makes these comparisons worth making.
 *
 * All are drawn on a 64×64 grid, single-weight strokes, `currentColor` so they
 * inherit the section's hue.
 */

const OPEN = `<svg viewBox="0 0 64 64" class="wicon" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">`;

/** The data center itself: a plan view of long halls behind a fence line. */
export const iconFacility = `${OPEN}
  <rect x="9" y="20" width="46" height="9" rx="1.5" />
  <rect x="9" y="33" width="46" height="9" rx="1.5" />
  <rect x="9" y="46" width="30" height="9" rx="1.5" />
  <path d="M6 14h52" stroke-dasharray="3 3" opacity="0.6" />
  <circle cx="47" cy="50.5" r="4" />
  <path d="M47 47.5v3l2 1.5" />
</svg>`;

/** Golf: a flag on a green, with the pin shadow. */
export const iconGolf = `${OPEN}
  <path d="M24 47V13l17 6.5-17 6.5" />
  <ellipse cx="32" cy="49" rx="19" ry="6" />
  <path d="M24 47v2" />
</svg>`;

/** A typical course: the same flag, smaller, with a second fairway line. */
export const iconGolfMedian = `${OPEN}
  <path d="M27 45V18l13 5-13 5" />
  <ellipse cx="32" cy="47" rx="14" ry="4.5" />
  <path d="M11 55c8-3 34-3 42 0" opacity="0.55" />
</svg>`;

/** The town: a water tower over a roofline. */
export const iconTown = `${OPEN}
  <path d="M23 12h18l-2.5 12h-13z" />
  <path d="M27 24v18M37 24v18" />
  <path d="M22 42h20" />
  <path d="M6 56v-9l7-5 7 5v9" />
  <path d="M44 56v-9l7-5 7 5v9" />
  <path d="M3 56h58" />
</svg>`;

/** Snowmaking: a hill with a fan gun and its plume. */
export const iconSnow = `${OPEN}
  <path d="M4 50l16-22 10 13 7-8 23 17z" />
  <circle cx="17" cy="17" r="5" />
  <path d="M17 22v6" />
  <path d="M24 13l7-3M24 20l7 3M22.5 8.5l3-5" opacity="0.7" />
</svg>`;

/** Irrigated cropland: furrows under a pivot arm. */
export const iconCrop = `${OPEN}
  <path d="M5 52c9-4 45-4 54 0" />
  <path d="M7 44c9-3.5 41-3.5 50 0" opacity="0.8" />
  <path d="M10 36c8-3 36-3 44 0" opacity="0.6" />
  <path d="M32 30V14" />
  <path d="M14 14h36" />
  <path d="M20 14v5M32 14v5M44 14v5" opacity="0.7" />
</svg>`;

/**
 * Comparator id → mark. Unmapped comparators simply render without one, so a
 * new comparison never breaks the layout waiting for artwork.
 */
export const WATER_ICONS: Record<string, string> = {
  golf_pine_island: iconGolf,
  golf_median: iconGolfMedian,
  city_water: iconTown,
  ski_snowmaking: iconSnow,
  irrigated_acres: iconCrop,
};
